import i18next from 'i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats, ModuleType } from 'infrastructure/enums';
import { formatAuthorName } from 'utils/formatters';
import { capitalize } from 'utils';

import NoteCommentsColumn from './noteComment';

import type {
  IClinicNotes,
  IClinicNotesAuthor,
} from 'infrastructure/interfaces';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type { TDeleteCommentFn } from 'infrastructure/hooks/rpm';

export interface ColumnsProps {
  expandedRecord?: {
    expanded: boolean;
    guid: string;
  };
  deleteNote: TDeleteCommentFn;
  module?: ModuleType;
}

export const columns = ({
  expandedRecord,
  deleteNote,
  module,
}: ColumnsProps): IBaseTableColumns<IClinicNotes> => {
  const col: IBaseTableColumns<IClinicNotes> = [
    {
      label: i18next.t('labels.dateTime'),
      key: 'createdAt',
      width: '120px',
      render(value: string) {
        return (
          <span data-cy="clinic-notes-date">
            {DateFormatter({
              date: value,
              format: DateFormats['MM-dd-yyyy h:mm a'],
            })}
          </span>
        );
      },
    },
    {
      label: i18next.t('labels.electronicallySignedBy'),
      key: 'author',
      width: '300px',
      render(author: IClinicNotesAuthor) {
        return (
          <span data-cy="clinic-notes-author">
            {formatAuthorName({
              title: author.title,
              firstName: author.firstName,
              lastName: author.lastName,
              professionType: author.profession,
            })}
          </span>
        );
      },
    },
    {
      label: i18next.t('labels.noteComment'),
      key: 'note',
      width: '700px',
      responsive: ['md'],
      render(value: string, record) {
        return (
          <NoteCommentsColumn
            value={value}
            record={record}
            deleteNote={deleteNote}
            expandedRecord={expandedRecord}
          />
        );
      },
    },
  ];

  if (module === ModuleType.APCM) {
    col.push({
      label: i18next.t('labels.type'),
      key: 'templateType',
      width: '80px',
      align: 'center',
      render(value: string) {
        return (
          <span data-cy="clinical-notes-template-type">
            {capitalize(value)}
          </span>
        );
      },
    });
  }

  return col;
};
